/**
 * Frontend API client for the Attempt Engine endpoints (Module 1).
 *
 * Thin transport — no business logic, no caching. The TanStack-Query
 * hooks layer that wraps this client arrives with the broader Module 11
 * frontend-state slice; for 06-attempt-engine/01 the pre-screen page
 * calls these functions directly.
 *
 * 11-iframe-embed/02: `PreScreenView` extended with `company_name` (Story D5)
 * and `getPreScreen` accepts an `embed` flag to append `?embed=1`.
 *
 * Mid-attempt call paths (recordAnswer, flagQuestion, getTimer, submitAttempt)
 * use the 401-intercepting apiClient so session expiry triggers ReauthModal
 * instead of a bare redirect (J-X5 § Path A requirement).
 *
 * NOTE: getPreScreen and beginAttempt are pre-auth entry points. They use bare
 * fetch intentionally — sign-in.tsx POST also stays on bare fetch to avoid
 * 401-loop on the auth endpoint itself.
 */
import { createApiClient } from '../../lib/api-client.ts';

// Singleton intercepted fetch for mid-attempt endpoints.
// Constructed once per module load; safe to reuse across calls.
const apiClient = createApiClient();

export interface PreScreenView {
  test: {
    id: string;
    title: string;
    description: string;
    task_brief_md: string | null;
    task_artifact_url: string | null;
    estimated_minutes: number;
    time_limit: number;
    pass_mark: number;
    /** J-X22 — availability window ISO strings. Null/null = always-open. */
    availability_starts_at: string | null;
    availability_ends_at: string | null;
    availability_closed_reason: string | null;
  };
  token_mode: 'authenticated' | 'embedded';
  /** Story D5: company name for the privacy notice in embed mode. */
  company_name?: string;
}

export interface BeginAttemptBody {
  share_token: string;
  candidate_name?: string;
  candidate_email?: string;
  /** J-X20 — integrity pledge attestation. Required to be true server-side. */
  integrity_pledge_accepted?: boolean;
  integrity_pledge_version?: string;
}

export interface BeginAttemptResult {
  attempt_id: string;
  test: {
    id: string;
    title: string;
    pass_mark: number;
    show_result: boolean;
    sections: Array<{ name: string | null; question_ids: string[] }>;
  };
  questions: Array<{
    id: string;
    type: string;
    text: string;
    image_url: string | null;
    section_name: string | null;
    options?: Array<{ id: string; text: string; sort_order: number }>;
  }>;
  effective_time_limit_seconds: number;
  timer_started_at: string;
  shuffle_seed?: number;
}

export interface AttemptErrorEnvelope {
  code: string;
  message: string;
  reason?: string;
  field?: string;
  locked_until?: string;
}

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: AttemptErrorEnvelope };

export async function getPreScreen(
  baseUrl: string,
  token: string,
  opts?: { embed?: boolean },
): Promise<ApiResult<PreScreenView>> {
  const url = opts?.embed
    ? `${baseUrl}/api/take/${encodeURIComponent(token)}?embed=1`
    : `${baseUrl}/api/take/${encodeURIComponent(token)}`;
  let res: Response;
  try {
    res = await fetch(url, { credentials: 'include' });
  } catch {
    return { ok: false, error: { code: 'network', message: 'Network error — could not reach the server.' } };
  }
  try {
    return (await res.json()) as ApiResult<PreScreenView>;
  } catch {
    return { ok: false, error: { code: 'network', message: `server returned ${res.status} ${res.statusText}` } };
  }
}

export async function beginAttempt(
  baseUrl: string,
  body: BeginAttemptBody,
): Promise<ApiResult<BeginAttemptResult>> {
  let res: Response;
  try {
    res = await fetch(`${baseUrl}/api/attempts/begin`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'include',
    });
  } catch {
    return { ok: false, error: { code: 'network', message: 'Network error — could not reach the server.' } };
  }
  try {
    return (await res.json()) as ApiResult<BeginAttemptResult>;
  } catch {
    return { ok: false, error: { code: 'network', message: `server returned ${res.status} ${res.statusText}` } };
  }
}

// ─── Question Phase API (06-attempt-engine/02) ────────────────────────────────

export interface RecordAnswerBody {
  question_id: string;
  answer: string;
  idempotency_key?: string;
  client_version?: number;
}

export interface RecordAnswerResult {
  saved_at: string;
  server_version: number;
}

export interface TimerResult {
  seconds_remaining: number;
}

export interface AttemptResultView {
  attempt_id: string;
  test: {
    id: string;
    title: string;
    pass_mark: number;
    category?: string | null;
    version?: string | null;
  };
  score: number;
  passed: boolean;
  correct_count: number;
  total_questions: number;
  duration_seconds: number;
  completed_at: string;
  started_at?: string;
  candidate_name?: string;
  candidate_email?: string;
  certificate_id: string | null;
  certificate_download_url?: string | null;
  per_section: Array<{
    section_name: string | null;
    correct: number;
    total: number;
    points_earned: number;
    points_possible: number;
  }>;
  per_question: Array<{
    question_id: string;
    type: string;
    question_text?: string;
    section_name?: string | null;
    image_url?: string | null;
    user_answer: string;
    correct_answer: string;
    is_correct: boolean;
    points: number;
    explanation: string | null;
    options?: Array<{
      id: string;
      text: string;
      is_correct: boolean;
      sort_order?: number;
    }>;
  }>;
}

export async function recordAnswer(
  baseUrl: string,
  attemptId: string,
  body: RecordAnswerBody,
): Promise<ApiResult<RecordAnswerResult>> {
  // Uses apiClient (401-interceptor) — mid-attempt call path per J-X5 § Path A.
  let res: Response;
  try {
    res = await apiClient(`${baseUrl}/api/attempts/${attemptId}/answers`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'include',
    });
  } catch {
    return { ok: false, error: { code: 'network', message: 'Network error — answer could not be saved.' } };
  }
  try {
    return (await res.json()) as ApiResult<RecordAnswerResult>;
  } catch {
    return { ok: false, error: { code: 'network', message: `server returned ${res.status} ${res.statusText}` } };
  }
}

export async function flagQuestion(
  baseUrl: string,
  attemptId: string,
  questionId: string,
  flagged: boolean,
): Promise<ApiResult<{ flagged: boolean }>> {
  // Uses apiClient (401-interceptor) — mid-attempt call path per J-X5 § Path A.
  let res: Response;
  try {
    res = await apiClient(`${baseUrl}/api/attempts/${attemptId}/flag`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ question_id: questionId, flagged }),
      credentials: 'include',
    });
  } catch {
    return { ok: false, error: { code: 'network', message: 'Network error — flag could not be saved.' } };
  }
  try {
    return (await res.json()) as ApiResult<{ flagged: boolean }>;
  } catch {
    return { ok: false, error: { code: 'network', message: `server returned ${res.status} ${res.statusText}` } };
  }
}

export async function getTimer(
  baseUrl: string,
  attemptId: string,
): Promise<ApiResult<TimerResult>> {
  // Uses apiClient (401-interceptor) — mid-attempt call path per J-X5 § Path A.
  let res: Response;
  try {
    res = await apiClient(`${baseUrl}/api/attempts/${attemptId}/timer`, {
      credentials: 'include',
    });
  } catch {
    return { ok: false, error: { code: 'network', message: 'Network error — timer could not be fetched.' } };
  }
  try {
    return (await res.json()) as ApiResult<TimerResult>;
  } catch {
    return { ok: false, error: { code: 'network', message: `server returned ${res.status} ${res.statusText}` } };
  }
}

export async function submitAttempt(
  baseUrl: string,
  attemptId: string,
  submitter: 'candidate' | 'timer' | 'system' = 'candidate',
): Promise<ApiResult<AttemptResultView>> {
  // Uses apiClient (401-interceptor) — mid-attempt call path per J-X5 § Path A.
  let res: Response;
  try {
    res = await apiClient(`${baseUrl}/api/attempts/${attemptId}/submit`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ submitter }),
      credentials: 'include',
    });
  } catch {
    return { ok: false, error: { code: 'network', message: 'Network error — submission could not be sent. Please try again.' } };
  }
  try {
    return (await res.json()) as ApiResult<AttemptResultView>;
  } catch {
    return { ok: false, error: { code: 'network', message: `server returned ${res.status} ${res.statusText}` } };
  }
}

export async function getAttemptResult(
  baseUrl: string,
  attemptId: string,
): Promise<ApiResult<AttemptResultView>> {
  let res: Response;
  try {
    res = await fetch(`${baseUrl}/api/attempts/${attemptId}/result`, {
      credentials: 'include',
    });
  } catch {
    return { ok: false, error: { code: 'network', message: 'Network error — could not reach the server.' } };
  }
  try {
    return (await res.json()) as ApiResult<AttemptResultView>;
  } catch {
    return { ok: false, error: { code: 'network', message: `server returned ${res.status} ${res.statusText}` } };
  }
}
